/**
 * What a finished run found: the needs (no client names), the run's notices (partial, over budget, no strong match,
 * and files the run did not read in full, by name), the matches best first, how they fit together, and the near misses.
 */

import { AlertTriangle, Info } from 'lucide-react';

import type { AdvisorNeeds, AdvisorResults, AdvisorRun } from '@/api/types';

import { MatchCard } from './MatchCard';

export function NeedsCard({ needs }: { needs: AdvisorNeeds }) {
  return (
    <section className="card" aria-labelledby="advisor-needs-title">
      <div className="card-body stack-md">
        <h2 id="advisor-needs-title" className="advisor-title">What the client needs</h2>
        {needs.summary && <p>{needs.summary}</p>}
        <ul className="advisor-list">{needs.needs.map((n, i) => <li key={i}>{n}</li>)}</ul>
        {needs.constraints.length > 0 && (
          <p className="hint">Constraints: {needs.constraints.join('; ')}</p>
        )}
        <p className="hint">
          Industry: {needs.industry ?? 'Not stated'} · Country: {needs.country ?? 'Not stated'}
        </p>
      </div>
    </section>
  );
}

function Notice({ tone, title, children }: { tone: 'warning' | 'info'; title: string; children: string }) {
  return (
    <div className={`alert alert-${tone}`} role="note">
      {tone === 'warning' ? <AlertTriangle aria-hidden="true" /> : <Info aria-hidden="true" />}
      <div>
        <div className="a-title">{title}</div>
        <div className="a-body">{children}</div>
      </div>
    </div>
  );
}

function FilesNotice({ notice }: { notice: string | null }) {
  return notice ? <Notice tone="info" title="Files not read in full">{notice}</Notice> : null;
}

export function Notices({ results }: { results: AdvisorResults }) {
  if (results.status === 'complete') return <FilesNotice notice={results.notice} />;
  if (results.status === 'partial') {
    const count = results.unevaluated;
    const note = `${results.notice ?? 'Some assets could not be evaluated.'}${count ? ` ${count} ${count === 1 ? 'asset was' : 'assets were'} not evaluated.` : ''}`;
    return <Notice tone="warning" title="Partial run">{note}</Notice>;
  }
  if (results.status === 'over_budget') {
    return <Notice tone="warning" title="Over budget">{results.notice ?? 'The monthly AI budget is used up.'}</Notice>;
  }
  if (results.status === 'no_match') {
    return (
      <>
        <Notice tone="info" title="No strong match">
          No asset scored high enough to propose. The nearest ones are below; the need is recorded as unmatched demand for the portfolio owners.
        </Notice>
        <FilesNotice notice={results.notice} />
      </>
    );
  }
  return null;
}

function Synthesis({ results }: { results: AdvisorResults }) {
  if (!results.ranking_notes && results.combinations.length === 0) return null;
  return (
    <section className="card" aria-labelledby="advisor-synthesis-title">
      <div className="card-body stack-md">
        <h2 id="advisor-synthesis-title" className="advisor-title">How they fit together</h2>
        {results.ranking_notes && <p>{results.ranking_notes}</p>}
        {results.combinations.length > 0 && (
          <ul className="advisor-list">
            {results.combinations.map((c, i) => <li key={i}><b>{c.names.join(' and ')}</b>: {c.why}</li>)}
          </ul>
        )}
      </div>
    </section>
  );
}

export function RunResults({ run }: { run: AdvisorRun }) {
  const { results, needs } = run;
  return (
    <>
      {needs && <NeedsCard needs={needs} />}
      {results && <Notices results={results} />}
      {results && results.matches.length > 0 && (
        <section className="stack-md" aria-labelledby="advisor-matches-title">
          <h2 id="advisor-matches-title" className="advisor-title">Matching assets</h2>
          {results.matches.map((m) => <MatchCard key={m.asset_id} match={m} />)}
        </section>
      )}
      {results && <Synthesis results={results} />}
      {results && results.near_misses.length > 0 && (
        <section className="stack-md" aria-labelledby="advisor-near-title">
          <h2 id="advisor-near-title" className="advisor-title">Near misses</h2>
          {results.near_misses.map((m) => <MatchCard key={m.asset_id} match={m} nearMiss />)}
        </section>
      )}
    </>
  );
}
